import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { BehaviorSubject, combineLatest, firstValueFrom, map } from 'rxjs';
import { InstellingBron, LicentieRol, RInstelling, RInstellingAdditionalObjectKey } from '@cumlaude/service-contract';
import { AsyncPipe } from '@angular/common';
import { ButtonComponent } from '@cumlaude/shared-components-buttons';
import { InstantSearchBoxComponent, Option, SingleSelectDropdownComponent } from '@cumlaude/shared-components-inputs';
import { RestService } from '@cumlaude/shared-services';
import { toObservable } from '@angular/core/rxjs-interop';
import { find, isUndefined, startCase } from 'lodash-es';
import { Router } from '@angular/router';
import { InstellingenListComponent } from '../../components/instellingen-list/instellingen-list.component';
import { IconComponent } from '@cumlaude/shared-components-icons';
import { getTimestamp, includesIgnoreCaseAndDiacritics, isDefined } from '@cumlaude/shared-utils';
import { saveAs } from 'file-saver';

@Component({
	selector: 'app-instellingen-page',
	templateUrl: './instellingen-page.component.html',
	styleUrls: ['./instellingen-page.component.scss'],
	imports: [AsyncPipe, ButtonComponent, InstantSearchBoxComponent, InstellingenListComponent, SingleSelectDropdownComponent, IconComponent],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InstellingenPageComponent {
	private readonly restService = inject(RestService);
	private readonly router = inject(Router);

	labels = [new Option(undefined, '<Alle>'), ...Object.values(InstellingBron).map((label) => new Option<string>(label)), new Option<string>('VSO')];
	searchInput$ = new BehaviorSubject<string>('');

	selectedLabel = signal<string | undefined>(undefined);

	instellingen$ = combineLatest([
		this.restService.getInstellingen(undefined, [RInstellingAdditionalObjectKey.LICENTIES, RInstellingAdditionalObjectKey.VESTIGING_NAMEN]),
		this.searchInput$,
		toObservable(this.selectedLabel),
	]).pipe(map(([instellingen, searchInput, selectedLabel]) => this.filterInstellingen(instellingen, searchInput, selectedLabel)));

	vestigingen$ = this.restService.getVestigingen({ cumlaudeActief: true });

	private filterInstellingen(instellingen: RInstelling[], searchInput: string, selectedLabel: string | undefined) {
		return instellingen
			.filter((instelling) => {
				const vestigingNamen = instelling.additionalObjects?.[RInstellingAdditionalObjectKey.VESTIGING_NAMEN]?.namen ?? [];
				return (
					includesIgnoreCaseAndDiacritics(instelling.naam, searchInput) ||
					includesIgnoreCaseAndDiacritics(instelling.bestuur?.naam ?? '', searchInput) ||
					vestigingNamen.some((naam) => includesIgnoreCaseAndDiacritics(naam, searchInput))
				);
			})
			.filter((instelling) => {
				if (isUndefined(selectedLabel)) return true;

				if (selectedLabel === 'VSO') return instelling.vso;
				else if ((<string[]>Object.values(InstellingBron)).includes(selectedLabel)) return selectedLabel === instelling.bron;
				else return instelling.bestuur?.naam === selectedLabel;
			});
	}

	openInstellingToevoegen() {
		this.router.navigate(['instelling', 'new']);
	}

	gotoInstelling(instelling: RInstelling) {
		this.router.navigate(['instelling', instelling.id]);
	}

	gotoInstellingOnNewTab(instelling: RInstelling) {
		window.open(this.router.serializeUrl(this.router.createUrlTree(['instelling', instelling.id])), '_blank');
	}

	getSelected(labels: Option<string | undefined>[], selectedLabel: string | undefined) {
		return find(labels, (option) => option.value === selectedLabel)!;
	}

	async export() {
		const instellingen = await firstValueFrom(this.instellingen$);
		const vestigingen = await firstValueFrom(this.vestigingen$);

		let csvContent =
			[
				'Naam instelling',
				'Bron',
				'Bestuur',
				'Status',
				'School (gebruikt)',
				'School (totaal)',
				'Sectie (gebruikt)',
				'Sectie (totaal)',
				'Docent',
				'Aanwezigheid',
				'Naam vestiging',
				'VSO vestiging',
				'Afgenomen modules',
				'Aantal leerlingen',
			].join(';') + '\n';

		csvContent += instellingen
			.flatMap((instelling) =>
				vestigingen.filter((vestiging) => vestiging.instelling.id === instelling.id).map((vestiging) => ({ instelling, vestiging }))
			)
			.map(({ instelling, vestiging }) =>
				[
					`"${instelling.naam}"`,
					instelling.bron,
					`"${instelling.bestuur?.naam ?? ''}"`,
					instelling.actief ? 'Actief' : 'Inactief',
					...this.getLicenties(instelling, LicentieRol.SCHOOLBREEDTEAM),
					...this.getLicenties(instelling, LicentieRol.SECTIE),
					...this.getLicenties(instelling, LicentieRol.DOCENTMENTOR),
					...this.getLicenties(instelling, LicentieRol.AANWEZIGHEID),
					`"${vestiging.naam}"`,
					vestiging.vso ? 'Ja' : 'Nee',
					`${vestiging.modules.map((module) => startCase(module.toLowerCase())).join(', ')}`,
					`${vestiging.aantalLeerlingen1Okt ?? ''}`,
				].join(';')
			)
			.join('\n');

		saveAs(new Blob([csvContent], { type: 'text/csv;charset=utf-8;' }), `CumLaude instellingen ${getTimestamp()}.csv`);
	}

	getLicenties(instelling: RInstelling, rol: LicentieRol): string[] {
		const data = instelling.additionalObjects![RInstellingAdditionalObjectKey.LICENTIES]?.[rol];
		if (!isDefined(data)) return [LicentieRol.DOCENTMENTOR, LicentieRol.AANWEZIGHEID].includes(rol) ? [``] : [``, ``];

		switch (rol) {
			case LicentieRol.SCHOOLBREEDTEAM:
			case LicentieRol.SECTIE:
				return isDefined(data.totaal) ? [`${data.inGebruik}`, `${data.totaal}`] : [`${data.inGebruik}`, ``];
			case LicentieRol.DOCENTMENTOR:
			case LicentieRol.AANWEZIGHEID:
				return [`${data.inGebruik}`];
			default:
				throw new Error(`Onbekende licentie rol: ${rol}`);
		}
	}
	protected readonly LicentieRol = LicentieRol;
}
